import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  LinearProgress,
  Alert,
  Rating,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Tabs,
  Tab,
  Container,
  Pagination,
  CircularProgress,
  TablePagination
} from "@mui/material";
import {
  Search,
  Inventory,
  LocalOffer,
  Star,
  ArrowBack,
  TrendingUp,
  Add,
  Edit,
  Delete,
  Warning,
  TrendingDown,
  SwapHoriz,
} from "@mui/icons-material";
import { medicinesAPI, suppliersAPI } from "../../services/API";
import { useSnackbar } from "notistack";

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
  </div>
);

export const SupplierProducts = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [medicines, setMedicines] = useState([]);
  const [allMedicines, setAllMedicines] = useState([]);
  const [filteredMedicines, setFilteredMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [returnDialog, setReturnDialog] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    medicineId: "",
    costPrice: "",
    minOrderQuantity: 1,
    notes: "",
  });
  const [returnData, setReturnData] = useState({
    returnQuantity: 1,
    returnReason: "",
    damageType: "none",
    creditAmount: 0,
  });

  // Pagination states for all medicines in dialog
  const [allMedicinesPage, setAllMedicinesPage] = useState(1);
  const [allMedicinesLimit] = useState(50);
  const [allMedicinesTotal, setAllMedicinesTotal] = useState(0);
  const [allMedicinesLoading, setAllMedicinesLoading] = useState(false);

  // Pagination states for main table
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchMedicines();
    fetchAllMedicines();
  }, [id]);

  useEffect(() => {
    const filtered = medicines.filter(
      (medicine) =>
        medicine.medicine?.name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        medicine.medicine?.category
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        medicine.medicine?.sku?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredMedicines(filtered);
    setPage(0); // Reset to first page when searching
  }, [searchTerm, medicines]);

  const fetchMedicines = async () => {
    try {
      const response = await suppliersAPI.getMedicines(id);
      setMedicines(response.data.data.medicines || []);
    } catch (error) {
      enqueueSnackbar("Error fetching supplier medicines", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAllMedicines = async (page = 1) => {
    try {
      setAllMedicinesLoading(true);
      const response = await medicinesAPI.getAll({
        page,
        limit: allMedicinesLimit,
        isActive: true
      });
      const { medicines: medicinesData, pagination } = response.data.data;
      
      if (page === 1) {
        setAllMedicines(medicinesData || []);
      } else {
        setAllMedicines(prev => [...prev, ...(medicinesData || [])]);
      }
      setAllMedicinesTotal(pagination?.total || 0);
    } catch (error) {
      enqueueSnackbar("Error fetching medicines", { variant: "error" });
    } finally {
      setAllMedicinesLoading(false);
    }
  };

  const loadMoreMedicines = () => {
    const nextPage = allMedicinesPage + 1;
    setAllMedicinesPage(nextPage);
    fetchAllMedicines(nextPage);
  };

  const handleAddMedicine = async () => {
    try {
      await suppliersAPI.addMedicine(id, formData);
      enqueueSnackbar("Medicine added to supplier successfully", {
        variant: "success",
      });
      setAddDialog(false);
      setFormData({
        medicineId: "",
        costPrice: "",
        minOrderQuantity: 1,
        notes: "",
      });
      fetchMedicines();
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Error adding medicine to supplier",
        { variant: "error" }
      );
    }
  };

  const handleEditMedicine = async () => {
    try {
      await suppliersAPI.updateMedicine(id, selectedMedicine._id, formData);
      enqueueSnackbar("Medicine updated successfully", { variant: "success" });
      setEditDialog(false);
      setSelectedMedicine(null);
      setFormData({
        medicineId: "",
        costPrice: "",
        minOrderQuantity: 1,
        notes: "",
      });
      fetchMedicines();
    } catch (error) {
      enqueueSnackbar("Error updating medicine", { variant: "error" });
    }
  };

  const handleDeleteMedicine = async (medicineId) => {
    try {
      await suppliersAPI.removeMedicine(id, medicineId);
      enqueueSnackbar("Medicine removed from supplier successfully", {
        variant: "success",
      });
      fetchMedicines();
    } catch (error) {
      enqueueSnackbar("Error removing medicine from supplier", {
        variant: "error",
      });
    }
  };

  const handleReturnMedicine = async () => {
    try {
      const creditAmount = selectedMedicine.costPrice * returnData.returnQuantity;

      await suppliersAPI.addMedicineReturn(id, selectedMedicine._id, {
        ...returnData,
        creditAmount,
      });

      enqueueSnackbar("Medicine return recorded successfully", {
        variant: "success",
      });
      setReturnDialog(false);
      setSelectedMedicine(null);
      setReturnData({
        returnQuantity: 1,
        returnReason: "",
        damageType: "none",
        creditAmount: 0,
      });
      fetchMedicines();
    } catch (error) {
      enqueueSnackbar("Error recording medicine return", { variant: "error" });
    }
  };

  // Table pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStockStatus = (stock) => {
    if (stock === 0) return { label: "Out of Stock", color: "error" };
    if (stock < 10) return { label: "Low Stock", color: "warning" };
    return { label: "In Stock", color: "success" };
  };

  const getProfitMarginColor = (margin) => {
    if (margin > 50) return "success";
    if (margin > 20) return "primary";
    if (margin > 0) return "warning";
    return "error";
  };

  const getReturnRateColor = (rate) => {
    if (rate < 2) return "success";
    if (rate < 5) return "warning";
    return "error";
  };

  const calculateMetrics = () => {
    const activeMedicines = medicines.filter((m) => m.isActive);
    const totalMedicines = medicines.length;
    const totalValue = medicines.reduce(
      (sum, m) => sum + m.costPrice * (m.medicine?.stock || 0),
      0
    );
    const avgProfitMargin = medicines.length > 0
      ? medicines.reduce((acc, m) => acc + (m.profitMargin || 0), 0) / medicines.length
      : 0;
    const totalReturns = medicines.reduce(
      (acc, m) => acc + (m.returnQuantity || 0),
      0
    );
    const returnRate = medicines.length > 0
      ? (totalReturns / medicines.reduce((acc, m) => acc + (m.totalQuantity || 1), 0)) * 100
      : 0;

    return {
      totalMedicines,
      activeMedicines: activeMedicines.length,
      totalValue,
      avgProfitMargin,
      returnRate,
    };
  };

  const metrics = calculateMetrics();

  // Filter out medicines that are already added to the supplier
  const availableMedicines = allMedicines.filter(
    medicine => !medicines.some(supplierMed => 
      supplierMed.medicine?._id === medicine._id
    )
  );

  // Paginated data for main table
  const paginatedMedicines = filteredMedicines.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
        fontSize: '16px',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate("/hospital/pharmacy/suppliers")}
              variant="outlined"
              sx={{ fontSize: '1.1rem', px: 3, py: 1 }}
            >
              Back to Suppliers
            </Button>
            <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
              Supplier Products
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setAddDialog(true);
              setAllMedicinesPage(1);
              fetchAllMedicines(1);
            }}
            sx={{ fontSize: '1.1rem', px: 3, py: 1 }}
          >
            Add Medicine
          </Button>
        </Box>

        {/* Enhanced Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3, boxShadow: 3, transition: '0.3s', '&:hover': { boxShadow: 6 } }}>
              <CardContent sx={{ textAlign: "center", p: 3 }}>
                <Inventory sx={{ fontSize: 50, color: "primary.main", mb: 2 }} />
                <Typography variant="h2" fontWeight="bold" sx={{ fontSize: { xs: '2.5rem', md: '3rem' } }}>
                  {metrics.totalMedicines}
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem', mt: 1 }}>
                  Total Products
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3, boxShadow: 3, transition: '0.3s', '&:hover': { boxShadow: 6 } }}>
              <CardContent sx={{ textAlign: "center", p: 3 }}>
                <LocalOffer sx={{ fontSize: 50, color: "success.main", mb: 2 }} />
                <Typography variant="h2" fontWeight="bold" color="success.main" sx={{ fontSize: { xs: '2.5rem', md: '3rem' } }}>
                  {metrics.activeMedicines}
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem', mt: 1 }}>
                  Active Products
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3, boxShadow: 3, transition: '0.3s', '&:hover': { boxShadow: 6 } }}>
              <CardContent sx={{ textAlign: "center", p: 3 }}>
                <TrendingUp sx={{ fontSize: 50, color: "info.main", mb: 2 }} />
                <Typography variant="h2" fontWeight="bold" color="info.main" sx={{ fontSize: { xs: '2.5rem', md: '3rem' } }}>
                  {Math.round(metrics.avgProfitMargin)}%
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem', mt: 1 }}>
                  Avg Profit Margin
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3, boxShadow: 3, transition: '0.3s', '&:hover': { boxShadow: 6 } }}>
              <CardContent sx={{ textAlign: "center", p: 3 }}>
                <TrendingDown sx={{ fontSize: 50, color: "warning.main", mb: 2 }} />
                <Typography variant="h2" fontWeight="bold" color="warning.main" sx={{ fontSize: { xs: '2.5rem', md: '3rem' } }}>
                  {metrics.returnRate.toFixed(1)}%
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem', mt: 1 }}>
                  Return Rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ borderRadius: 3, boxShadow: 3, transition: '0.3s', '&:hover': { boxShadow: 6 } }}>
              <CardContent sx={{ textAlign: "center", p: 3 }}>
                <Star sx={{ fontSize: 50, color: "secondary.main", mb: 2 }} />
                <Typography variant="h2" fontWeight="bold" color="secondary.main" sx={{ fontSize: { xs: '2.5rem', md: '3rem' } }}>
                  ₹{(metrics.totalValue / 1000).toFixed(1)}K
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem', mt: 1 }}>
                  Inventory Value
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Tabs */}
        <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search medicines by name, category, or SKU..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: '1.5rem' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '1.1rem',
                  py: 2,
                },
              }}
            />
          </CardContent>
        </Card>

        {/* Enhanced Medicines Table with Pagination */}
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
                Product Catalog
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                Showing {paginatedMedicines.length} of {filteredMedicines.length} products
              </Typography>
            </Box>

            {filteredMedicines.length === 0 ? (
              <Alert severity="info" sx={{ fontSize: '1.1rem', py: 2 }}>
                No medicines found for this supplier.
              </Alert>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Product Details</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Pricing</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Stock Info</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Performance</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Status</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedMedicines.map((item) => (
                        <TableRow key={item._id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ py: 2.5 }}>
                            <Box>
                              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
                                {item.medicine?.name}
                              </Typography>
                              <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}>
                                {item.medicine?.manufacturer}
                              </Typography>
                              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                <Chip
                                  label={item.medicine?.category}
                                  size="medium"
                                  variant="outlined"
                                  sx={{ fontSize: { xs: '0.7rem', md: '0.9rem' } }}
                                />
                                <Typography
                                  variant="body1"
                                  color="text.secondary"
                                  sx={{ fontSize: { xs: '0.7rem', md: '0.9rem' } }}
                                >
                                  SKU: {item.medicine?.sku}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell sx={{ py: 2.5 }}>
                            <Box>
                              <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}>
                                Cost Price
                              </Typography>
                              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
                                ₹{item.costPrice?.toLocaleString()}
                              </Typography>
                              <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{ mt: 1, fontSize: { xs: '0.8rem', md: '1rem' } }}
                              >
                                Selling Price
                              </Typography>
                              <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
                                ₹{item.medicine?.sellingPrice?.toLocaleString() || "N/A"}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell sx={{ py: 2.5 }}>
                            <Box>
                              <Chip
                                label={getStockStatus(item.medicine?.stock).label}
                                size="medium"
                                color={getStockStatus(item.medicine?.stock).color}
                                sx={{ fontSize: { xs: '0.7rem', md: '0.9rem' } }}
                              />
                              <Typography variant="body1" sx={{ mt: 1, fontSize: { xs: '0.8rem', md: '1rem' } }}>
                                Stock: {item.medicine?.stock || 0}
                              </Typography>
                              <Typography variant="body1" sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}>
                                Min Order: {item.minOrderQuantity}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell sx={{ py: 2.5 }}>
                            <Box>
                              {item.profitMargin && (
                                <Chip
                                  label={`${item.profitMargin.toFixed(1)}% Margin`}
                                  size="medium"
                                  color={getProfitMarginColor(item.profitMargin)}
                                  variant="outlined"
                                  sx={{ fontSize: { xs: '0.7rem', md: '0.9rem' } }}
                                />
                              )}
                              {item.returnRate && (
                                <Chip
                                  label={`${item.returnRate}% Returns`}
                                  size="medium"
                                  color={getReturnRateColor(item.returnRate)}
                                  sx={{ mt: 0.5, fontSize: { xs: '0.7rem', md: '0.9rem' } }}
                                />
                              )}
                              <Rating
                                value={item.medicine?.rating || 0}
                                size="small"
                                readOnly
                                sx={{ mt: 1 }}
                              />
                            </Box>
                          </TableCell>

                          <TableCell sx={{ py: 2.5 }}>
                            <Box>
                              <Chip
                                label={item.isActive ? "Active" : "Inactive"}
                                size="medium"
                                color={item.isActive ? "success" : "default"}
                                sx={{ fontSize: { xs: '0.7rem', md: '0.9rem' } }}
                              />
                              {item.lastOrderDate && (
                                <Typography
                                  variant="body1"
                                  display="block"
                                  sx={{ mt: 1, fontSize: { xs: '0.7rem', md: '0.9rem' } }}
                                >
                                  Last: {new Date(item.lastOrderDate).toLocaleDateString()}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>

                          <TableCell sx={{ py: 2.5 }}>
                            <Box sx={{ display: "flex", gap: 0.5, flexDirection: "column" }}>
                              <Tooltip title="Edit Product">
                                <IconButton
                                  size="large"
                                  onClick={() => {
                                    setSelectedMedicine(item);
                                    setFormData({
                                      medicineId: item.medicine?._id,
                                      costPrice: item.costPrice,
                                      minOrderQuantity: item.minOrderQuantity,
                                      notes: item.notes || "",
                                    });
                                    setEditDialog(true);
                                  }}
                                  color="primary"
                                >
                                  <Edit sx={{ fontSize: '1.5rem' }} />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Record Return">
                                <IconButton
                                  size="large"
                                  onClick={() => {
                                    setSelectedMedicine(item);
                                    setReturnData({
                                      returnQuantity: 1,
                                      returnReason: "",
                                      damageType: "none",
                                      creditAmount: item.costPrice,
                                    });
                                    setReturnDialog(true);
                                  }}
                                  color="warning"
                                >
                                  <SwapHoriz sx={{ fontSize: '1.5rem' }} />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Remove Product">
                                <IconButton
                                  size="large"
                                  onClick={() => handleDeleteMedicine(item._id)}
                                  color="error"
                                >
                                  <Delete sx={{ fontSize: '1.5rem' }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Table Pagination */}
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredMedicines.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: { xs: '0.8rem', md: '1rem' }
                    }
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>

        {/* Add Medicine Dialog - UPDATED with Larger Font Sizes */}
        <Dialog
          open={addDialog}
          onClose={() => setAddDialog(false)}
          maxWidth="md"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              height: '80vh',
              fontSize: '16px'
            }
          }}
        >
          <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600, pb: 2 }}>
            Add Medicine to Supplier
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: '1.1rem' }}>Select Medicine</InputLabel>
                  <Select
                    value={formData.medicineId}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        medicineId: e.target.value,
                      }))
                    }
                    label="Select Medicine"
                    sx={{ fontSize: '1.1rem' }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    }}
                  >
                    <MenuItem value="" disabled sx={{ fontSize: '1.1rem' }}>
                      Select Medicine
                    </MenuItem>
                    {availableMedicines.map((medicine) => (
                      <MenuItem key={medicine._id} value={medicine._id} sx={{ fontSize: '1.1rem' }}>
                        {medicine.name} - {medicine.genericName} (SKU: {medicine.sku})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {/* Load More Button */}
                {allMedicines.length < allMedicinesTotal && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button 
                      onClick={loadMoreMedicines} 
                      disabled={allMedicinesLoading}
                      variant="outlined"
                      size="large"
                      sx={{ fontSize: '1rem' }}
                    >
                      {allMedicinesLoading ? (
                        <CircularProgress size={24} />
                      ) : (
                        `Load More (${allMedicines.length} of ${allMedicinesTotal})`
                      )}
                    </Button>
                  </Box>
                )}

                {/* Info about already added medicines */}
                {medicines.length > 0 && (
                  <Alert severity="info" sx={{ mt: 2, fontSize: '1.1rem' }}>
                    {medicines.length} medicines already added to this supplier. 
                    Only available medicines are shown.
                  </Alert>
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Cost Price"
                  value={formData.costPrice}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      costPrice: parseFloat(e.target.value) || 0,
                    }))
                  }
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ fontSize: '1.1rem' }}>₹</InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                    '& .MuiInputBase-input': { fontSize: '1.1rem' },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minimum Order Quantity"
                  value={formData.minOrderQuantity}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      minOrderQuantity: parseInt(e.target.value) || 1,
                    }))
                  }
                  inputProps={{ min: 1 }}
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                    '& .MuiInputBase-input': { fontSize: '1.1rem' },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Supplier Notes"
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder="Any specific notes about this supplier for this medicine..."
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                    '& .MuiInputBase-input': { fontSize: '1.1rem' },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => setAddDialog(false)} 
              sx={{ fontSize: '1.1rem', px: 3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddMedicine}
              variant="contained"
              disabled={!formData.medicineId || !formData.costPrice}
              sx={{ fontSize: '1.1rem', px: 3 }}
            >
              Add Medicine
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Medicine Dialog with Larger Font Sizes */}
        <Dialog
          open={editDialog}
          onClose={() => setEditDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600, pb: 2 }}>
            Edit Product Details
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2, fontSize: '1.1rem' }}>
                  Editing: {selectedMedicine?.medicine?.name}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Cost Price"
                  value={formData.costPrice}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      costPrice: parseFloat(e.target.value) || 0,
                    }))
                  }
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ fontSize: '1.1rem' }}>₹</InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                    '& .MuiInputBase-input': { fontSize: '1.1rem' },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minimum Order Quantity"
                  value={formData.minOrderQuantity}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      minOrderQuantity: parseInt(e.target.value) || 1,
                    }))
                  }
                  inputProps={{ min: 1 }}
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                    '& .MuiInputBase-input': { fontSize: '1.1rem' },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Supplier Notes"
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                    '& .MuiInputBase-input': { fontSize: '1.1rem' },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => setEditDialog(false)} 
              sx={{ fontSize: '1.1rem', px: 3 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleEditMedicine} 
              variant="contained"
              sx={{ fontSize: '1.1rem', px: 3 }}
            >
              Update Product
            </Button>
          </DialogActions>
        </Dialog>

        {/* Return Medicine Dialog with Larger Font Sizes */}
        <Dialog
          open={returnDialog}
          onClose={() => setReturnDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600, pb: 2 }}>
            Record Product Return
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mb: 2, fontSize: '1.1rem' }}>
                  Returning: {selectedMedicine?.medicine?.name}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Return Quantity"
                  value={returnData.returnQuantity}
                  onChange={(e) => {
                    const quantity = parseInt(e.target.value) || 0;
                    const creditAmount = quantity * (selectedMedicine?.costPrice || 0);
                    setReturnData((prev) => ({
                      ...prev,
                      returnQuantity: quantity,
                      creditAmount,
                    }));
                  }}
                  inputProps={{
                    min: 1,
                    max: selectedMedicine?.medicine?.stock || 1,
                  }}
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                    '& .MuiInputBase-input': { fontSize: '1.1rem' },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: '1.1rem' }}>Damage Type</InputLabel>
                  <Select
                    value={returnData.damageType}
                    onChange={(e) =>
                      setReturnData((prev) => ({
                        ...prev,
                        damageType: e.target.value,
                      }))
                    }
                    label="Damage Type"
                    sx={{ fontSize: '1.1rem' }}
                  >
                    <MenuItem value="none" sx={{ fontSize: '1.1rem' }}>No Damage</MenuItem>
                    <MenuItem value="expired" sx={{ fontSize: '1.1rem' }}>Expired</MenuItem>
                    <MenuItem value="damaged" sx={{ fontSize: '1.1rem' }}>Physically Damaged</MenuItem>
                    <MenuItem value="wrong_item" sx={{ fontSize: '1.1rem' }}>Wrong Item</MenuItem>
                    <MenuItem value="quality_issue" sx={{ fontSize: '1.1rem' }}>Quality Issue</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Return Reason"
                  value={returnData.returnReason}
                  onChange={(e) =>
                    setReturnData((prev) => ({
                      ...prev,
                      returnReason: e.target.value,
                    }))
                  }
                  placeholder="Detailed reason for return..."
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                    '& .MuiInputBase-input': { fontSize: '1.1rem' },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ fontSize: '1.1rem' }}>
                  Credit Amount: ₹{returnData.creditAmount.toLocaleString()}
                </Alert>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => setReturnDialog(false)} 
              sx={{ fontSize: '1.1rem', px: 3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReturnMedicine}
              variant="contained"
              color="warning"
              disabled={!returnData.returnQuantity || !returnData.returnReason}
              sx={{ fontSize: '1.1rem', px: 3 }}
            >
              Record Return
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};